import React, { useContext } from "react";
import { useModalContext } from "@/shared/components/modal/contexts/modal-context";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { useTradesContext } from "@/features/accounts/shared/contexts/trades-context";
import { OcrProcessStatus } from "@/shared/config/globals"
import { useInstitutionsContext } from "@/features/institutions/contexts/institutions-context";
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { useTradeJumpContext } from "@/features/trade-jump/contexts/trade-jump-context";
import { useAccountPollingContext } from "@/features/accounts/shared/contexts/account-polling-context";
import { ErrorDialogContext } from "@/shared/context/error-context";
import { MoneyLinkWarning } from "@/shared/utils/custom-exception";


interface Props {
  isOwner: boolean;
  passbookType: string;
  authorizationStatus: number;
  onTapCheckOcr: () => void;
  ocrStatus: number | undefined;
}

export const ViewTradesSidebar = ({isOwner, passbookType, authorizationStatus, onTapCheckOcr, ocrStatus}: Props) => {
  const {
      activeInvalid,
      setActiveInvalid,
      activeBalance,
      setActiveBalance,
    } = useTradesContext();
  const { setOpenHomeModal } = useModalContext();
  const { setCompanyCode } = useInstitutionsContext();
  const { selectedAccount } = useAccountGroupsContext();
  const { syncJump } = useTradeJumpContext();

  const { setError } = useContext(ErrorDialogContext);
  const { pollingInfos } = useAccountPollingContext();
  const isPolling = pollingInfos.some(info =>
    info.mlinkAccountId === selectedAccount?.mlinkAccountId &&
    info.mlinkId === selectedAccount?.mlinkId &&
    info.passbookType === passbookType
  );

  // OCR処理中または完了時に警告ダイアログを表示
  const showOcrWarningIfNeeded = (onProceed: () => void) => {
    if (ocrStatus === OcrProcessStatus.Completed || ocrStatus === OcrProcessStatus.Processing) {
      setError(new MoneyLinkWarning({
        title: "OCR読み取り結果の確認",
        message: "読み取り結果を確認し、必要に応じて修正してください。",
      }));
      return;
    }
    onProceed();
  };

  return (
    <nav className="nav-list">
      <BaseRoundButton
        className="btn-search open-modal"
        onClick={() => {
          setOpenHomeModal("ConditionalSearch");
        }}
        buttonText={<span>検索</span>}
      />

      {(   passbookType === "01"
        || passbookType === "09"
        || passbookType === "20"
        || passbookType === "201"
        || passbookType === "202"
        || passbookType === "101"
      ) && (
        <BaseRoundButton
          className="btn-yen"
          onClick={() => {
            setActiveBalance(!activeBalance);
          }}
          buttonText={<span>残高</span>}
        />
      )}

      {!syncJump && isOwner && (
      <BaseRoundButton
        className="btn-toggle"
        onClick={() => {
          setActiveInvalid(!activeInvalid);
        }}
        buttonText={<span>仕訳対象</span>}
      />
      )}

      {(passbookType === "201" && !syncJump && isOwner) && (
        <BaseRoundButton
          className="btn-input open-modal"
          onClick={() => {
            setCompanyCode(selectedAccount?.companyCode || "");
            setOpenHomeModal("UploadCsv");
          }}
          buttonText={<span>明細を追加</span>}
        />
      )}

      {(passbookType === "202" && !syncJump && isOwner) && (
        <BaseRoundButton
          className="btn-input open-modal"
          onClick={() => {
            setOpenHomeModal("UploadExcel");
          }}
          buttonText={<span>明細を追加</span>}
        />
      )}

      {(passbookType === "101" && !syncJump && isOwner) && (
        <BaseRoundButton
          className={ocrStatus === OcrProcessStatus.Processing ? "ic-load" : "open-modal btn-ocr"}
          onClick={() => showOcrWarningIfNeeded(() => setOpenHomeModal("AddOcrTrades"))}
          buttonText={<span>OCR読込</span>}
        />
      )}

      {/* カメラによるOCR読み込みクラス名など仮置き */}
      {(passbookType === "101" && !syncJump && isOwner) && (
        <BaseRoundButton
        className={ocrStatus === OcrProcessStatus.Processing ? "ic-load" : "open-modal btn-camera"}
          onClick={() => showOcrWarningIfNeeded(() => setOpenHomeModal("Camera"))}
          buttonText={<span>カメラ</span>}
        />
      )}

      {(passbookType === "101" && !syncJump) && (
        <BaseRoundButton
          className={`btn-checkocr${ocrStatus === OcrProcessStatus.Completed ? " new" : ""}`}
          buttonText={<span>OCR確認</span>}
          onClick={() => {
            onTapCheckOcr();
          }}
          isActive={ocrStatus === OcrProcessStatus.Completed}
        />
      )}

      <BaseRoundButton
        className="btn-download"
        onClick={() => {
          setOpenHomeModal("Download");
        }}
        buttonText={<span>Excel出力</span>}
      />

      {(   passbookType === "01" 
        || passbookType === "19659" 
        || passbookType === "19660" 
        || passbookType === "03" 
        || passbookType === "10" 
        || passbookType === "20") 
        && authorizationStatus !== 4
        && !syncJump
        && isOwner && (
          <BaseRoundButton
            className={isPolling ? "ic-load" : "open-modal btn-reset"}
            onClick={() => {
              setOpenHomeModal("UpdateTrades");
            }}
            isActive={!isPolling && authorizationStatus !== 2 && !selectedAccount?.isUnderMaintenance}
            buttonText={<span>明細を更新</span>}
          />
      )}

      <BaseRoundButton  
        className="btn-menu sub1st open-modal"
        onClick={() => {
          setOpenHomeModal("Option");
        }}
        buttonText={<span>オプション</span>}
      />
    </nav>
  );
};
